import { useMemo } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

// Import highcharts-more for polar charts (side-effect import)
import 'highcharts/highcharts-more'

interface WindData {
  windDirection: number
  windSpeed: number
}

interface WindRoseChartProps {
  data: WindData[]
  title?: string
  userSpeedUnit?: string
}

// Speed conversion helper (from m/s to user preference)
const convertSpeedMS = (speedMS: number, unit: string = 'ms'): string => {
  switch (unit.toLowerCase()) {
    case 'mph':
      return (speedMS * 2.23694).toFixed(1)
    case 'kmh':
    case 'kph':
      return (speedMS * 3.6).toFixed(1)
    case 'kts':
    case 'knots':
      return (speedMS * 1.94384).toFixed(1)
    default:
      return speedMS.toFixed(1)
  }
}

export const WindRoseChart = ({ data, title = 'Wind Rose', userSpeedUnit = 'ms' }: WindRoseChartProps) => {
  const options = useMemo(() => {
    // 1) Normalize & filter data
    const rawData = data
      .map(p => ({
        wind_direction: parseFloat(String(p.windDirection)),
        wind_speed: parseFloat(String(p.windSpeed))
      }))
      .filter(p => Number.isFinite(p.wind_direction) && Number.isFinite(p.wind_speed))

    // 2) Define bins & labels
    const speedBins = [
      [0, 2],   // Calm → Light breeze
      [2, 5],   // Gentle → Moderate breeze
      [5, 8],   // Fresh breeze
      [8, 12],  // Strong breeze
      [12, 16], // Near gale
      [16, 40]  // Gale / Storm / Gusts
    ]
    const dirLabels = [
      'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
      'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
    ]

    // 3) Tally frequencies
    const freq = speedBins.map(() => Array(dirLabels.length).fill(0))
    rawData.forEach(p => {
      const dIdx = Math.floor((p.wind_direction + 11.25) / 22.5) % 16
      const sIdx = speedBins.findIndex(b => p.wind_speed >= b[0] && p.wind_speed < b[1])
      if (sIdx >= 0) {
        freq[sIdx][dIdx]++
      }
    })

    // 4) Calculate percentages
    const total = rawData.length || 1
    const seriesData = speedBins.map((b, i) => {
      const minSpeed = convertSpeedMS(b[0], userSpeedUnit)
      const maxSpeed = convertSpeedMS(b[1], userSpeedUnit)
      const unitLabel = userSpeedUnit === 'ms' ? 'm/s' :
                       userSpeedUnit === 'mph' ? 'mph' :
                       userSpeedUnit === 'kmh' ? 'km/h' :
                       userSpeedUnit === 'kts' ? 'knots' : 'm/s'

      return {
        name: `${minSpeed} - ${maxSpeed} ${unitLabel}`,
        data: freq[i].map(c => parseFloat((c / total * 100).toFixed(1))),
        pointPlacement: 'on'
      }
    })

    // 5) Return Highcharts config
    return {
      chart: {
        polar: true,
        type: 'column',
        height: 500,
        spacingBottom: 5,
        spacingTop: 20
      },
      credits: { enabled: false },
      title: {
        text: title,
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      pane: { size: '85%' },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemMarginTop: 6,
        itemMarginBottom: 4
      },
      xAxis: {
        categories: dirLabels,
        tickmarkPlacement: 'on',
        lineWidth: 0
      },
      yAxis: {
        min: 0,
        endOnTick: false,
        showLastLabel: true,
        title: { text: 'Frequency (%)' },
        labels: { format: '{value}%' },
        reversedStacks: false
      },
      tooltip: { valueSuffix: '%' },
      plotOptions: {
        series: {
          stacking: 'normal',
          shadow: false,
          groupPadding: 0
        }
      },
      series: seriesData
    }
  }, [data, title, userSpeedUnit])

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[500px] text-muted-foreground">
        No wind data available for wind rose visualization
      </div>
    )
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />
}
